import { GameState } from '../game/GameState';
import { DataRegistry } from './DataRegistry';
import { bfsPath } from '../utils/pathfinding';
import type { BattleResult } from '../types/battle';
import { ModifierManager } from './ModifierManager';
import { WarManager } from './WarManager';

export class ArmyManager {
  static readonly instance = new ArmyManager();

  armiesByFaction(factionId: string) {
    return Object.values(GameState.instance.requireCampaign().armies).filter((army) => army.factionId === factionId);
  }

  plannedPath(armyId: string) {
    const campaign = GameState.instance.requireCampaign();
    const army = campaign.armies[armyId];
    if (!army?.destinationProvinceId || army.destinationProvinceId === army.provinceId) return [];
    return bfsPath(DataRegistry.instance.provinces, army.provinceId, army.destinationProvinceId);
  }

  estimatedMonths(armyId: string) {
    const path = this.plannedPath(armyId);
    return Math.max(0, path.length - 1);
  }

  moveArmy(armyId: string, targetProvinceId: string) {
    const campaign = GameState.instance.requireCampaign();
    const army = campaign.armies[armyId];
    if (!army) return false;
    const activeSiege = WarManager.instance.siegeForProvince(army.provinceId);
    if (activeSiege?.besiegerFactionId === army.factionId && activeSiege.progress < 100) {
      GameState.instance.log(`${army.id} must complete the siege before marching onward.`, 'warning');
      return false;
    }
    const path = bfsPath(DataRegistry.instance.provinces, army.provinceId, targetProvinceId);
    if (path.length < 2) {
      GameState.instance.log('No route.', 'warning');
      return false;
    }
    const nextProvinceId = path[1];
    if (!this.canEnterProvince(army.factionId, nextProvinceId) && !this.escalateClaimOnEntry(army.factionId, nextProvinceId)) {
      GameState.instance.log('Movement requires ownership, war, alliance, or military access.', 'warning');
      return false;
    }

    army.provinceId = nextProvinceId;
    campaign.selectedArmyId = armyId;
    campaign.selectedProvinceId = army.provinceId;

    const targetName = DataRegistry.instance.provinces[targetProvinceId]?.name ?? targetProvinceId;
    const nextName = DataRegistry.instance.provinces[nextProvinceId]?.name ?? nextProvinceId;
    const enemyFort = this.isHostileFort(army.factionId, nextProvinceId);
    if (enemyFort) {
      delete army.destinationProvinceId;
      WarManager.instance.beginSiege(army.id);
      GameState.instance.log(`${army.id} halted before the fortifications of ${nextName}.`, 'warning');
    } else if (nextProvinceId === targetProvinceId) {
      delete army.destinationProvinceId;
      GameState.instance.log(`Army arrived in ${nextName}.`, 'success');
    } else {
      army.destinationProvinceId = targetProvinceId;
      GameState.instance.log(`Army marches toward ${targetName} via ${nextName}; ${path.length - 2} month${path.length - 2 === 1 ? '' : 's'} remain.`);
    }

    const province = campaign.provinces[army.provinceId];
    if (province && province.owner !== army.factionId && WarManager.instance.areEnemies(army.factionId, province.owner)) {
      GameState.instance.log((province.fortLevel ?? 0) > 0 ? 'Enemy fortifications reached. A siege has begun.' : 'Enemy territory reached. Battle or occupation is available.', 'danger');
    }
    return true;
  }

  advancePlannedMovements() {
    const campaign = GameState.instance.requireCampaign();
    for (const army of Object.values(campaign.armies)) {
      const destination = army.destinationProvinceId;
      if (!destination) continue;
      if (destination === army.provinceId) {
        delete army.destinationProvinceId;
        continue;
      }
      const activeSiege = WarManager.instance.siegeForProvince(army.provinceId);
      if (activeSiege?.besiegerFactionId === army.factionId && activeSiege.progress < 100) {
        delete army.destinationProvinceId;
        continue;
      }

      const path = bfsPath(DataRegistry.instance.provinces, army.provinceId, destination);
      if (path.length < 2) {
        delete army.destinationProvinceId;
        GameState.instance.log(`${army.id} could not continue its march.`, army.factionId === campaign.playerFactionId ? 'warning' : 'info');
        continue;
      }

      const nextProvinceId = path[1];
      if (!this.canEnterProvince(army.factionId, nextProvinceId) && !this.escalateClaimOnEntry(army.factionId, nextProvinceId)) {
        delete army.destinationProvinceId;
        if (army.factionId === campaign.playerFactionId) GameState.instance.log(`${army.id}'s march was blocked by diplomatic access.`, 'warning');
        continue;
      }

      army.provinceId = nextProvinceId;
      const enemyFort = this.isHostileFort(army.factionId, nextProvinceId);
      if (enemyFort) {
        delete army.destinationProvinceId;
        WarManager.instance.beginSiege(army.id);
      } else if (nextProvinceId === destination) {
        delete army.destinationProvinceId;
        GameState.instance.log(`${army.id} arrived in ${DataRegistry.instance.provinces[nextProvinceId]?.name ?? nextProvinceId}.`, army.factionId === campaign.playerFactionId ? 'success' : 'info');
      } else if (army.factionId === campaign.playerFactionId) {
        GameState.instance.log(`${army.id} reached ${DataRegistry.instance.provinces[nextProvinceId]?.name ?? nextProvinceId}; ${path.length - 2} month${path.length - 2 === 1 ? '' : 's'} remain.`);
      }
    }
  }

  canEnterProvince(factionId: string, provinceId: string) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    const faction = campaign.factions[factionId];
    if (!province || !faction) return false;
    if (province.owner === factionId || province.controller === factionId) return true;
    if (WarManager.instance.areEnemies(factionId, province.owner)) return true;
    if (faction.treaties.alliances.includes(province.owner) || faction.treaties.militaryAccess.includes(province.owner)) return true;
    return false;
  }

  armyStrength(armyId: string) {
    const campaign = GameState.instance.requireCampaign();
    const army = campaign.armies[armyId];
    if (!army) return 0;
    const modifiers = ModifierManager.instance.forFaction(army.factionId);
    const commander = army.commanderId ? campaign.commanders[army.commanderId] : undefined;
    const commandMultiplier = 1 + (commander?.martial ?? 0) * 0.035 + campaign.factions[army.factionId].militaryTradition * 0.002;
    return army.units.reduce((sum, stack) => {
      const unit = DataRegistry.instance.units[stack.unitId];
      const base = ((unit?.attack ?? 1) + (unit?.defense ?? 1) + (unit?.morale ?? 30) / 10) * Math.max(1, stack.strength);
      const gunpowder = ModifierManager.instance.hasGunpowderEdge(army.factionId, unit) ? modifiers.battleAttackMultiplier : 1;
      return sum + base * gunpowder * (1 + stack.experience / 100) * commandMultiplier;
    }, 0);
  }

  resolveCampaignSkirmish(attackerArmyId: string) {
    const campaign = GameState.instance.requireCampaign();
    const attacker = campaign.armies[attackerArmyId];
    if (!attacker) return false;
    const province = campaign.provinces[attacker.provinceId];
    if (!province || province.owner === attacker.factionId || !WarManager.instance.areEnemies(attacker.factionId, province.owner)) return false;
    const defenders = this.defendersForProvince(province.id, attacker.factionId);
    if (!defenders.length) {
      if ((province.fortLevel ?? 0) > 0 && province.controller !== attacker.factionId) return WarManager.instance.beginSiege(attacker.id);
      const occupied = WarManager.instance.occupyProvince(province.id, attacker.factionId);
      if (occupied) {
        campaign.factions[attacker.factionId].prestige += 1;
        GameState.instance.log(`${DataRegistry.instance.factions[attacker.factionId]?.name ?? attacker.factionId} occupies ${DataRegistry.instance.provinces[province.id]?.name}.`, attacker.factionId === campaign.playerFactionId ? 'success' : 'danger');
      }
      return occupied;
    }

    const defenderStrength = defenders.reduce((sum, army) => sum + this.armyStrength(army.id), 0)
      + (province.fortLevel ?? 0) * (60 + ModifierManager.instance.forFaction(province.owner).fortDefenseBonus);
    const attackerStrength = this.armyStrength(attacker.id);
    const attackerWins = attackerStrength * 1.08 >= defenderStrength;
    const result: BattleResult = {
      winnerFactionId: attackerWins ? attacker.factionId : province.owner,
      loserFactionId: attackerWins ? province.owner : attacker.factionId,
      provinceId: province.id,
      attackerLosses: Math.round(attacker.units.length * (attackerWins ? 8 : 22)),
      defenderLosses: Math.round(defenders.reduce((sum, army) => sum + army.units.length, 0) * (attackerWins ? 24 : 10)),
      attackerArmyIds: [attacker.id],
      defenderArmyIds: defenders.map((army) => army.id),
      routed: true,
    };
    this.applyBattleResult(result);
    GameState.instance.log(`Campaign battle resolved in ${DataRegistry.instance.provinces[province.id]?.name}.`, attackerWins ? 'success' : 'danger');
    return true;
  }

  defendersForProvince(provinceId: string, attackerFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    return Object.values(campaign.armies).filter((army) => army.provinceId === provinceId && WarManager.instance.areEnemies(army.factionId, attackerFactionId));
  }

  applyBattleResult(result: BattleResult) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[result.provinceId];
    const winner = campaign.factions[result.winnerFactionId];
    const loser = campaign.factions[result.loserFactionId];
    if (!province || !winner || !loser) return;

    winner.prestige += result.decisive ? 6 : 4;
    loser.prestige = Math.max(0, loser.prestige - (result.decisive ? 5 : 3));
    loser.warExhaustion = Math.min(100, loser.warExhaustion + (result.decisive ? 8 : 5));
    if (winner.activeLaws.includes('war_council_edict')) winner.legitimacy = Math.min(100, winner.legitimacy + 1);

    const inferredAttacker = result.attackerArmyIds?.map((id) => campaign.armies[id]?.factionId).find(Boolean)
      ?? (result.winnerFactionId !== province.owner ? result.winnerFactionId : result.loserFactionId);
    const inferredDefender = inferredAttacker === result.winnerFactionId ? result.loserFactionId : result.winnerFactionId;
    const attackerIds = new Set(result.attackerArmyIds?.length
      ? result.attackerArmyIds
      : Object.values(campaign.armies).filter((army) => army.provinceId === result.provinceId && army.factionId === inferredAttacker).map((army) => army.id));
    const defenderIds = new Set(result.defenderArmyIds?.length
      ? result.defenderArmyIds
      : Object.values(campaign.armies).filter((army) => army.provinceId === result.provinceId && army.factionId === inferredDefender).map((army) => army.id));
    const attackerTotal = Math.max(1, result.attackerLosses + (result.attackerRemaining ?? 0));
    const defenderTotal = Math.max(1, result.defenderLosses + (result.defenderRemaining ?? 0));
    const attackerRate = result.attackerRemaining === undefined
      ? (inferredAttacker === result.winnerFactionId ? 0.12 : 0.28)
      : Math.min(0.85, result.attackerLosses / attackerTotal);
    const defenderRate = result.defenderRemaining === undefined
      ? (inferredDefender === result.winnerFactionId ? 0.12 : 0.28)
      : Math.min(0.85, result.defenderLosses / defenderTotal);

    for (const army of Object.values(campaign.armies)) {
      const attacker = attackerIds.has(army.id);
      const defender = defenderIds.has(army.id);
      if (!attacker && !defender) continue;
      const lossRate = attacker ? attackerRate : defenderRate;
      const victorious = army.factionId === result.winnerFactionId;
      for (const stack of army.units) {
        stack.strength = Math.max(0, Math.round(stack.strength * (1 - lossRate)));
        stack.morale = victorious
          ? Math.min(100, Math.max(result.attackerMorale ?? result.defenderMorale ?? stack.morale, stack.morale + 5 + ModifierManager.instance.forFaction(army.factionId).battleMoraleBonus))
          : Math.max(1, Math.min(stack.morale - (result.routed ? 16 : 10), result.attackerMorale ?? result.defenderMorale ?? stack.morale));
        if (victorious) stack.experience = Math.min(100, stack.experience + (result.decisive ? 6 : 4));
      }
      army.units = army.units.filter((stack) => stack.strength > 0);
      delete army.destinationProvinceId;
      if (!army.units.length) delete campaign.armies[army.id];
    }

    WarManager.instance.registerBattle(result);
    if (result.winnerFactionId !== province.owner) {
      if ((province.fortLevel ?? 0) > 0) {
        const winningArmy = Object.values(campaign.armies).find((army) => army.factionId === result.winnerFactionId && army.provinceId === province.id);
        if (winningArmy) WarManager.instance.beginSiege(winningArmy.id);
      } else {
        WarManager.instance.occupyProvince(province.id, result.winnerFactionId);
      }
    }
    GameState.instance.updateVictoryProgress();
  }

  private isHostileFort(factionId: string, provinceId: string) {
    const province = GameState.instance.requireCampaign().provinces[provinceId];
    if (!province || (province.fortLevel ?? 0) <= 0 || province.owner === factionId || province.controller === factionId) return false;
    return WarManager.instance.areEnemies(factionId, province.owner);
  }

  private escalateClaimOnEntry(factionId: string, provinceId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const province = campaign.provinces[provinceId];
    if (!faction || !province || !faction.claims.includes(provinceId)) return false;
    return WarManager.instance.declareWar(factionId, province.owner, 'reconquest', provinceId);
  }
}
